import { useState, useEffect, useCallback, useRef } from "react";
import apiClient from "../../../shared/API/apiClient";
import Scope2EmissionModal from "./Scope2EmissionModal";
import CSVErrorModal from "../csvErrorModal";
import DeleteModal from "../../../shared/components/DeleteModal";
import Toast from "../../../shared/components/Toast";
import { useReportingYear } from "../../../context/ReportingYearContext";
import Pagination from "../../../shared/components/Pagination";
import usePagination from "../../../shared/hooks/usePagination";

// ── CSV parser ─────────────────────────────────────────────────────────────
const parseCSV = (text) => {
  const lines = text
    .trim()
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = lines[0]
    .split(",")
    .map((h) => h.trim().toLowerCase().replace(/\s+/g, ""));
  const rows = lines.slice(1).map((line) => {
    const values = line.split(",").map((v) => v.trim());
    const obj = {};
    headers.forEach((h, i) => {
      obj[h] = values[i] ?? "";
    });
    return obj;
  });
  return { headers, rows };
};

// ── Client-side: column names + count only ────────────────────────────────
const EXPECTED_HEADERS = [
  "recorddate",
  "departmentname",
  "sitename",
  "electricity_consumed",
];

const validateHeaders = (headers) => {
  if (headers.length !== EXPECTED_HEADERS.length) {
    return `CSV must have exactly ${EXPECTED_HEADERS.length} columns. Found ${headers.length}.`;
  }
  for (const expected of EXPECTED_HEADERS) {
    if (!headers.includes(expected)) {
      return `Missing required column: "${expected}". Check spelling and spacing.`;
    }
  }
  return null;
};
// ──────────────────────────────────────────────────────────────────────────

const TOAST_DURATION = 5000;

const Scope2EmissionPage = () => {
  const [entries, setEntries] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [toast, setToast]               = useState(null);
  const [importing, setImporting]       = useState(false);
  const [csvErrors, setCsvErrors]       = useState([]);
  const [isAdmin, setIsAdmin]           = useState(false);
  const [isYearLocked, setIsYearLocked] = useState(true);

  const { reportingYear, isCurrentYear } = useReportingYear();

  const fileInputRef = useRef(null);
  const csvTimerRef = useRef(null);

  // ── Read admin flag from localStorage once on mount ───────────────────
useEffect(() => {
  try {
    const user = JSON.parse(localStorage.getItem("user") ?? "{}");

    setIsAdmin(
      ["Admin", "Preparer", "Super Admin"].includes(user?.role_name)
    );
  } catch (e) {
    setIsAdmin(false);
  }
}, []);

  useEffect(() => {
  const fetchYearLockStatus = async () => {
    try {
      const res = await apiClient.post("/api/reportingyear");
      const years = res.data?.data ?? [];

      const normalise = (val) =>
        (val ?? "").toString().trim().replace(/^fy/i, "").toLowerCase();

      const matched = years.find(
        (y) => normalise(y.year_label) === normalise(reportingYear)
      );

      // is_locked: 0 = not locked (editable), 1 = locked (read-only)
      setIsYearLocked(matched ? matched.is_locked === 1 : true);
    } catch (err) {
      console.error("fetchYearLockStatus error:", err);
      setIsYearLocked(true); // fail safe: stay read-only
    }
  };

  if (reportingYear) fetchYearLockStatus();
}, [reportingYear]);

  // cleanup CSV timer on unmount
  useEffect(() => () => clearTimeout(csvTimerRef.current), []);

  const showToast = (message, type = "success") => setToast({ message, type });

  // ── Pagination ────────────────────────────────────────────────────────
  const {
    currentPage,
    totalPages,
    pageData,
    goToPage,
    setPageSize,
    pageSize,
    totalItems,
    startIndex,
    endIndex,
  } = usePagination(entries, 10);

  // ── Fetch list ────────────────────────────────────────────────────────
  const fetchList = useCallback(async () => {
    if (!reportingYear) return;
    setLoadingList(true);
    try {
      const response = await apiClient.get("/api/scope2-list", {
        params: { reporting_year: reportingYear },
      });
      setEntries(response.data?.data ?? []);
    } catch {
      setEntries([]);
    } finally {
      setLoadingList(false);
    }
  }, [reportingYear]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  
  const isEntryReadOnly = ({ status_description = "" }) => {
    const status = status_description.toLowerCase();
    return ["read only", "locked", "readonly"].some((word) =>
      status.includes(word)
    );
  };

  const canAdd = isAdmin && !isYearLocked;

  const canEditDelete = (entry) =>
    isAdmin && !isYearLocked && !isEntryReadOnly(entry);

  console.log("yogita"+canEditDelete);
  const showActionsColumn = entries.some((e) => canEditDelete(e));
  const showActionButtons = showActionsColumn || (canAdd && entries.length === 0);
  // ── Modal helpers ─────────────────────────────────────────────────────
  const openAdd = () => {
    setEditRecord(null);
    setShowModal(true);
  };
  const openEdit = (r) => {
    setEditRecord(r);
    setShowModal(true);
  };
  const close = () => {
    setShowModal(false);
    setEditRecord(null);
  };

  const handleSaved = (response, wasEdit) => {
    showToast(
      response?.message || (wasEdit ? "Entry updated successfully!" : "Entry added successfully!"),"success"
    );
    close();
    fetchList();
  };

  const handleDeleted = (id , response) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
    setDeleteRecord(null);
    showToast(response?.message || "Entry deleted successfully.", "delete");
  };

  // ── Download sample CSV ───────────────────────────────────────────────
  const downloadSample = () => {
    const link = document.createElement("a");
    link.href = `${process.env.PUBLIC_URL}/scope2_import.csv`;
    link.download = "scope2_import.csv";
    link.click();
  };

  // ── Bulk CSV import ───────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file) return;

    const isCSV =
      file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");

    if (!isCSV) {
      showToast("Please select a valid CSV file (.csv).", "error");
      return;
    }

    const text = await file.text();
    const { headers, rows } = parseCSV(text);

    if (rows.length === 0) {
      showToast("CSV file is empty or has no data rows.", "error");
      return;
    }

    const headerError = validateHeaders(headers);
    if (headerError) {
      showToast(headerError, "error");
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiClient.post(
        "/api/scope2/bulk-csv-upload",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          params: { reporting_year: reportingYear },
        }
      );

      const { success_count = 0, failed_count = 0, total_rows = 0 } =
        response.data?.data ?? {};

      const failedDetails =
        response.data?.data?.details?.filter((d) => d.status === "failed") ??
        [];

      if (failed_count > 0 && failedDetails.length > 0) {
        showToast(
          response.data?.message || `${success_count} of ${total_rows} record(s) inserted successfully.`,
          "success"
        );

        const errors = failedDetails.map((d) => ({
          row: d.csv_row_number,
          message: d.error_message,
        }));

        clearTimeout(csvTimerRef.current);
        csvTimerRef.current = setTimeout(
          () => setCsvErrors(errors),
          TOAST_DURATION
        );

        if (success_count > 0) fetchList();
      } else {
        showToast(
          response.data?.message || `Import successful! ${success_count} record(s) added.`,
          "success"
        );
        fetchList();
      }
    } catch (err) {
      const serverMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Bulk import failed. Please check your file and try again.";
      showToast(serverMessage, "error");
    } finally {
      setImporting(false);
    }
  };

  const total = entries.reduce(
    (acc, e) =>
      acc +
      Number(e.grid_emission_factor ?? 0) * Number(e.electricity_consumed ?? 0),
    0
  );

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="dash-page">
      <div className="le-page-header">
        <div>
          <div className="dash-page-title">Scope 2 Emissions</div>
          <div className="dash-page-subtitle">
            Indirect GHG emissions from purchased electricity
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          <button className="org-btn-save le-add-btn" onClick={downloadSample}>
            Download Sample File
          </button>

          {showActionButtons && (
            <button
              className="org-btn-save le-add-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
            >
              {importing ? "Importing…" : "Import"}
            </button>
          )}

          {showActionButtons && (
            <button
              type="button"
              className="org-btn-save le-add-btn"
              onClick={openAdd}
            >
              + &nbsp;Add Entry
            </button>
          )}
        </div>
      </div>

      <div className="draft-box">
        {/* <span className="status">✏️ Draft</span> Draft — Editable */}
        {entries.length > 0 ? (
          <>
            <span className="status">✏️ {entries[0]?.status_name ?? "—"}</span>
          </>
        ) : (
          <span className="status">—</span>
        )}
      </div>

      <div className="le-card">
        {loadingList ? (
          <div className="le-empty">
            <div className="le-empty-text">Loading entries…</div>
          </div>
        ) : entries.length === 0 ? (
          <div className="le-empty">
            <div className="le-empty-text">
              No Scope 2 emission entries yet.
            </div>
            {showActionButtons && (
              <button className="le-empty-btn" onClick={openAdd}>
                + Add your first Entry
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="table-wrap">
              <table className="le-table">
                <thead>
                  <tr>
                    <th>RECORD DATE</th>
                    <th>REPORTING YEAR</th>
                    <th>DEPARTMENT</th>
                    <th>SITE</th>
                    <th>ELECTRICITY</th>
                    <th>UNIT</th>
                    <th>TCO₂e</th>
                    {showActionsColumn && <th>ACTIONS</th>}
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, i) => (
                    <tr key={e.id ?? i}>
                      <td data-label="RECORD DATE">{e.record_date}</td>
                      <td data-label="REPORTING YEAR">{e.reporting_year}</td>
                      <td data-label="DEPARTMENT">{e.department_name ?? "—"}</td>
                      <td data-label="SITE">{e.site_name ?? "—"}</td>
                      <td data-label="ELECTRICITY">
                        {parseFloat(e.electricity_consumed)
                          .toFixed(2)
                          .replace(/\.00$/, "")}
                      </td>
                      <td data-label="UNIT">{e.unit}</td>
                      <td data-label="TCO₂e">
                        {(
                          Number(e.grid_emission_factor ?? 0) *
                          Number(e.electricity_consumed ?? 0)
                        ).toFixed(2)}
                      </td>
                      {showActionsColumn && (
                        <td data-label="ACTIONS">
                          <div className="le-actions">
                            {canEditDelete(e) && (
                              <>
                                <button
                                  className="le-action-btn edit"
                                  onClick={() => openEdit(e)}
                                  title="Edit"
                                >
                                  ✏️ Edit
                                </button>
                                <button
                                  className="le-action-btn delete"
                                  onClick={() => setDeleteRecord(e)}
                                  title="Delete"
                                >
                                  🗑️ Delete
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              startIndex={startIndex}
              endIndex={endIndex}
              onPageChange={goToPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
            />

            <div style={{ textAlign: "right" }}>
              Total: {total.toFixed(2)} tCO₂e
            </div>
          </>
        )}
      </div>

      {showModal && (
        <Scope2EmissionModal
          onClose={close}
          onSaved={handleSaved}
          editRecord={editRecord}
          reportingYear={reportingYear}
        />
      )}

      {deleteRecord && (
        <DeleteModal
          record={deleteRecord}
          idKey="id"
          endpoint="/api/scope2-list-delete"
          onClose={() => setDeleteRecord(null)}
          onDeleted={handleDeleted}
        />
      )}

      {csvErrors.length > 0 && (
        <CSVErrorModal errors={csvErrors} onClose={() => setCsvErrors([])} />
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

export default Scope2EmissionPage;
